/**
 * Runtime registry for custom mention-badge renderers in the composer.
 *
 * By default every context mention renders as a neutral {@link ContextRefBadge}.
 * A plugin can register a custom component for its context kind so the
 * in-editor chip reflects live state — e.g. the GitHub plugin registers one
 * for `github-pr` that fetches the PR's state and colors the chip to match
 * the merged/closed/draft/open palette the transcript and agent header use.
 *
 * Same layering as {@link ./content-scanners} and
 * {@link ./context-url-matchers}: the registry lives in `@reflex/ui` (which
 * must not import from `web/` or a plugin), and the host resolves plugin
 * manifest declarations into real components and populates it.
 */
import type { ComponentType } from 'react';

/** Props every custom mention badge renderer receives. */
export interface ContextMentionRendererProps {
  contextKind: string;
  refId: string;
  title: string;
}

const renderers = new Map<string, ComponentType<ContextMentionRendererProps>>();

export function registerContextMentionRenderer(
  kind: string,
  Component: ComponentType<ContextMentionRendererProps>,
): void {
  renderers.set(kind, Component);
}

export function replaceContextMentionRenderers(
  next: Array<{ kind: string; Component: ComponentType<ContextMentionRendererProps> }>,
): void {
  renderers.clear();
  for (const entry of next) {
    renderers.set(entry.kind, entry.Component);
  }
}

export function getContextMentionRenderer(
  kind: string,
): ComponentType<ContextMentionRendererProps> | undefined {
  return renderers.get(kind);
}
